<template>
  <v-app>
    <v-main>
      <div class="login-page">

        <!-- Left brand panel (desktop only) -->
        <div class="brand-panel d-none d-md-flex">
          <div class="brand-content">

            <div class="d-flex align-center mb-8">
              <div class="brand-icon mr-3">
                <v-icon size="28" color="white">mdi-chart-line</v-icon>
              </div>
              <span class="brand-name">SaMo Sales Tracker</span>
            </div>

            <h1 class="brand-headline">Track. Manage.<br>Grow.</h1>

            <p class="brand-sub mt-4">
              Manage your sales, customers, products, payments, and business
              performance — all from one simple platform.
            </p>

            <!-- Feature pills -->
            <div class="feature-list mt-8">
              <div v-for="f in features" :key="f" class="feature-item">
                <v-icon small color="white" class="mr-2" style="opacity:0.9">mdi-check-circle-outline</v-icon>
                <span>{{ f }}</span>
              </div>
            </div>

            <!-- Decorative blobs -->
            <div class="blob blob-1" />
            <div class="blob blob-2" />
          </div>
        </div>

        <!-- Right form panel -->
        <div class="form-panel d-flex align-center justify-center">
          <div class="form-inner">

            <!-- Mobile logo -->
            <div class="d-flex d-md-none align-center justify-center mb-6">
              <div class="brand-icon-sm mr-2">
                <v-icon size="20" color="white">mdi-chart-line</v-icon>
              </div>
              <span class="mobile-brand-name">SaMo Sales Tracker</span>
            </div>

            <div class="mb-8">
              <h2 class="form-title">Welcome back</h2>
              <p class="form-subtitle">Sign in to your account to continue</p>
            </div>

            <v-alert v-if="errorMsg" type="error" dense text rounded="lg" class="mb-5">
              {{ errorMsg }}
              <span v-if="isInactive">
                — WhatsApp or call <a href="tel:0789298447" class="error--text font-weight-bold">078 929 8447</a>
              </span>
            </v-alert>

            <v-form ref="form" v-model="valid" @submit.prevent="handleLogin">

              <label class="field-label">Phone Number</label>
              <v-text-field
                v-model="phoneNumber"
                type="tel"
                placeholder="+27 00 000 0000"
                outlined
                dense
                rounded
                class="mt-1 mb-4"
                prepend-inner-icon="mdi-phone-outline"
                :rules="[v => !!v || 'Phone number is required']"
                hide-details="auto"
              />

              <label class="field-label">Password</label>
              <v-text-field
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="Enter your password"
                outlined
                dense
                rounded
                class="mt-1 mb-2"
                prepend-inner-icon="mdi-lock-outline"
                :append-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                @click:append="showPassword = !showPassword"
                :rules="[v => !!v || 'Password is required']"
                hide-details="auto"
              />

              <div class="mb-6" />

              <v-btn
                type="submit"
                color="primary"
                block
                large
                rounded
                :disabled="!valid"
                :loading="loading"
                class="sign-in-btn"
                elevation="0"
              >
                Sign In
                <v-icon right>mdi-arrow-right</v-icon>
              </v-btn>

            </v-form>

            <div class="text-center mt-6">
              <span class="text-body-2 grey--text">Don't have an account?</span>
              <v-btn text small color="primary" class="ml-1 font-weight-bold" @click="$router.push('/register')">
                Register
              </v-btn>
            </div>

            <p class="footer-note mt-10">
              © {{ new Date().getFullYear() }} SaMo Sales Tracker. All rights reserved.
            </p>

          </div>
        </div>

      </div>
    </v-main>
  </v-app>
</template>

<script>
import apiService from '@/http/apiService'

export default {
  name: 'HomeView',
  data () {
    return {
      valid: false,
      phoneNumber: '',
      password: '',
      showPassword: false,
      loading: false,
      errorMsg: '',
      isInactive: false,
      features: [
        'Sales & transaction tracking',
        'Customer & payment management',
        'Outstanding balance monitoring',
        'Product catalogue',
        'Business performance insights',
        'Role-based user access'
      ]
    }
  },
  methods: {
    async handleLogin () {
      if (!this.$refs.form.validate()) return
      this.loading = true
      this.errorMsg = ''
      this.isInactive = false
      try {
        const response = await apiService.logIn({
          phoneNumber: this.phoneNumber,
          password: this.password
        })
        this.$store.dispatch('setUser', response.data)
        localStorage.setItem('user', JSON.stringify(response.data))

        const permIds = (response.data?.permissions || []).map(p => p.id)
        const permittedRoutes = [
          { permId: 5, path: '/orders' },
          { permId: 2, path: '/daily-orders' },
          { permId: 3, path: '/products' },
          { permId: 4, path: '/daily-summary' }
        ]
        const first = permittedRoutes.find(r => permIds.includes(r.permId))
        // keep loading=true so the button stays in loading state during navigation
        await this.$router.push(first ? first.path : '/')
      } catch (err) {
        this.errorMsg =
          err.response?.data?.message ||
          err.response?.data ||
          'Login failed. Please check your credentials.'
        this.isInactive = this.errorMsg.toLowerCase().includes('inactive')
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.login-page {
  display: flex;
  min-height: 100vh;
  background: #f0f2f5;
}

/* ── Left brand panel ── */
.brand-panel {
  width: 48%;
  min-height: 100vh;
  background: linear-gradient(145deg, #0d47a1 0%, #1565c0 40%, #1976d2 75%, #1e88e5 100%);
  position: relative;
  overflow: hidden;
  flex-direction: column;
  justify-content: center;
  padding: 60px 56px;
}

.brand-content {
  position: relative;
  z-index: 2;
}

.brand-icon {
  width: 48px;
  height: 48px;
  background: rgba(255,255,255,0.15);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
}

.brand-name {
  color: white;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.3px;
}

.brand-headline {
  color: white;
  font-size: 48px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -1px;
}

.brand-sub {
  color: rgba(255,255,255,0.78);
  font-size: 15px;
  line-height: 1.7;
  max-width: 380px;
}

.feature-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.feature-item {
  display: flex;
  align-items: center;
  color: rgba(255,255,255,0.88);
  font-size: 14px;
}

/* Decorative blobs */
.blob {
  position: absolute;
  border-radius: 50%;
  background: rgba(255,255,255,0.06);
  z-index: 1;
}
.blob-1 {
  width: 420px;
  height: 420px;
  bottom: -120px;
  right: -100px;
}
.blob-2 {
  width: 220px;
  height: 220px;
  top: 40px;
  right: 60px;
}

/* ── Right form panel ── */
.form-panel {
  flex: 1;
  min-height: 100vh;
  padding: 40px 24px;
}

.form-inner {
  width: 100%;
  max-width: 400px;
}

.form-title {
  font-size: 28px;
  font-weight: 800;
  color: #1a1a2e;
  letter-spacing: -0.5px;
  line-height: 1.2;
}

.form-subtitle {
  color: #6b7280;
  font-size: 14px;
  margin-top: 6px;
}

.field-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.sign-in-btn {
  height: 48px !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  background: linear-gradient(135deg, #1565c0, #1e88e5) !important;
  box-shadow: 0 4px 16px rgba(21, 101, 192, 0.35) !important;
  transition: box-shadow 0.2s !important;
}
.sign-in-btn:hover {
  box-shadow: 0 6px 24px rgba(21, 101, 192, 0.5) !important;
}

.footer-note {
  text-align: center;
  font-size: 12px;
  color: #9ca3af;
}

/* Mobile logo */
.brand-icon-sm {
  width: 36px;
  height: 36px;
  background: #1976d2;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.mobile-brand-name {
  font-size: 16px;
  font-weight: 700;
  color: #1565c0;
}

/* Mobile: full width form */
@media (max-width: 959px) {
  .login-page {
    background: white;
  }
  .form-panel {
    padding: 48px 24px;
  }
}
</style>
